// Australian mobile handling for SMS two-factor.
//
// Shared rather than app-local because three places have to agree exactly: the
// enrolment screen validating what a vendor typed, the Supabase enroll() call
// that needs E.164, and the Send SMS hook handing Mobile Message a local
// number. A disagreement between any two of them shows up as a factor that
// enrols but never receives a code.
//
// Only MOBILES are accepted. This matters more here than it did for drivers:
// a vendor's contact_phone is a company contact and is frequently a landline,
// so rejecting non-mobiles is the common path, not an edge case.

const AU_MOBILE_LOCAL = /^04\d{8}$/;

/**
 * Canonicalise to local `04XXXXXXXX`, or null if it is not an AU mobile.
 *
 * Tolerant of the punctuation people actually type — spaces, dashes, dots,
 * parens — and of the `+61` / `61` international forms. Landlines, 13/1300
 * shortcodes and overseas numbers all return null: they cannot receive an SMS,
 * and accepting one would create a factor that can never be verified.
 */
export function normalizeAuPhone(raw: string): string | null {
  const digits = (raw ?? '').replace(/[\s()\-.]/g, '').replace(/^\+/, '');
  const local = digits.startsWith('61') ? `0${digits.slice(2)}` : digits;
  return AU_MOBILE_LOCAL.test(local) ? local : null;
}

/** Whether this is an AU mobile that could receive a code. */
export function isAuMobile(raw: string): boolean {
  return normalizeAuPhone(raw) !== null;
}

/**
 * Local form to the E.164 that Supabase's phone enrolment requires.
 * Returns null for anything that is not an AU mobile, so a landline can never
 * reach enroll().
 */
export function toE164(raw: string): string | null {
  const local = normalizeAuPhone(raw);
  return local === null ? null : `+61${local.slice(1)}`;
}

/**
 * Display form showing only the last three digits, e.g. `••• ••• 678`.
 *
 * Enough for a vendor to recognise their own number without printing it in
 * full on a shared screen. Anything too short to mask meaningfully is returned
 * as-is rather than dressed up as masked.
 */
export function maskPhone(raw: string): string {
  const digits = (raw ?? '').replace(/\D/g, '');
  return digits.length <= 3 ? raw : `••• ••• ${digits.slice(-3)}`;
}
